import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Save, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { HelpHint } from './HelpHint';

export type EditableElement = {
  id: string;
  parentId: string | null;
  type: string;
  name: string;
  identifier: string | null;
  status: string;
  areaId?: string | null;
};

/** The order equipment actually appears going out from a POP. */
const TYPES: Array<[string, string]> = [
  ['pop', 'POP — point of presence'],
  ['span', 'Span — backbone or distribution run'],
  ['olt', 'OLT'],
  ['switchbox', 'Switch box / cabinet'],
  ['fat', 'FAT — fibre access terminal'],
  ['bts', 'BTS — wireless base station'],
  ['cpe', 'CPE — wireless customer unit'],
  ['ont', 'ONT — customer fibre modem'],
];

const STATUSES: Array<[string, string]> = [
  ['up', 'Up — carrying traffic'],
  ['degraded', 'Degraded — working, but not right'],
  ['down', 'Down — not carrying traffic'],
  ['decommissioned', 'Decommissioned — retired, kept for history'],
];

/**
 * Adding and editing network equipment. Admin only.
 *
 * The tree was read-only: it could be seeded, and after that the only way to
 * record a new FAT box was a database client. For an ISP that adds equipment
 * most weeks, that meant the map drifted away from the ground almost
 * immediately — and the map is what incident impact, dispatch suppression and
 * the coverage figures are all computed from.
 *
 * Status is on the form rather than hidden behind a separate action because it
 * is the field that actually changes: an element is added once and marked down
 * many times.
 */
export function NetworkEditor({
  elements,
  areas,
  editing,
  onClose,
}: {
  elements: EditableElement[];
  areas: Array<{ id: string; name: string }>;
  /** An element to edit, or null to add a new one. */
  editing: EditableElement | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [form, setForm] = useState({
    type: editing?.type ?? 'fat',
    name: editing?.name ?? '',
    identifier: editing?.identifier ?? '',
    status: editing?.status ?? 'up',
    parentId: editing?.parentId ?? '',
    areaId: editing?.areaId ?? '',
  });

  // Reset when the caller switches which element is being edited; without this
  // the form keeps the previous element's values.
  useEffect(() => {
    setForm({
      type: editing?.type ?? 'fat',
      name: editing?.name ?? '',
      identifier: editing?.identifier ?? '',
      status: editing?.status ?? 'up',
      parentId: editing?.parentId ?? '',
      areaId: editing?.areaId ?? '',
    });
    setConfirmDelete(false);
    setError(null);
  }, [editing]);

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: ['network'] });
    onClose();
  };
  const fail = (caught: Error) => setError(caught.message);

  const body = () => ({
    type: form.type,
    name: form.name,
    identifier: form.identifier || null,
    status: form.status,
    parentId: form.parentId || null,
    areaId: form.areaId || null,
  });

  const save = useMutation({
    mutationFn: () =>
      editing ? api.updateNetworkElement(editing.id, body()) : api.createNetworkElement(body()),
    onSuccess: done,
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: () => api.deleteNetworkElement(editing!.id),
    onSuccess: done,
    onError: fail,
  });

  /*
   * A parent may be anything except the element itself or something already
   * below it — that would make the tree a ring. The API refuses either way;
   * leaving them out of the list means nobody has to be told off for trying.
   */
  const descendants = new Set<string>();
  if (editing) {
    const walk = (id: string) => {
      descendants.add(id);
      for (const child of elements.filter((e) => e.parentId === id)) walk(child.id);
    };
    walk(editing.id);
  }
  const parents = elements.filter((e) => !descendants.has(e.id));

  return (
    <section className="card border-primary-200">
      <div className="mb-3 flex items-start justify-between gap-3">
        <h3 className="flex items-center gap-1.5 font-heading text-lg">
          {editing ? `Edit ${editing.name}` : 'Add network element'}
          <HelpHint>
            This tree is what incident impact and coverage are worked out from. An element in the
            wrong place changes which customers a fault appears to affect.
          </HelpHint>
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted"
          aria-label="Close"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-sm font-medium">What is it</span>
          <select
            className="input w-full"
            value={form.type}
            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
          >
            {TYPES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium">Name</span>
          <input
            className="input w-full"
            value={form.name}
            placeholder="FAT-09"
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium">Identifier</span>
          <input
            className="input w-full"
            value={form.identifier}
            placeholder="Serial, circuit ID, or the label on the box"
            onChange={(e) => setForm((f) => ({ ...f, identifier: e.target.value }))}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium">State</span>
          <select
            className="input w-full"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
          >
            {STATUSES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <span className="mt-1 block text-xs text-muted-foreground">
            Marking this down shows everything below it as unreachable.
          </span>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium">Fed from</span>
          <select
            className="input w-full"
            value={form.parentId}
            onChange={(e) => setForm((f) => ({ ...f, parentId: e.target.value }))}
          >
            <option value="">Nothing — this is a root</option>
            {parents.map((element) => (
              <option key={element.id} value={element.id}>
                {element.name} ({element.type})
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium">Area</span>
          <select
            className="input w-full"
            value={form.areaId}
            onChange={(e) => setForm((f) => ({ ...f, areaId: e.target.value }))}
          >
            <option value="">Not set</option>
            {areas.map((area) => (
              <option key={area.id} value={area.id}>
                {area.name}
              </option>
            ))}
          </select>
          <span className="mt-1 block text-xs text-muted-foreground">
            A backbone run between two places can be left blank.
          </span>
        </label>
      </div>

      {error && <p className="mt-4 text-sm text-danger-600">{error}</p>}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="btn-primary"
          disabled={form.name.trim().length < 2 || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : editing ? (
            <Save className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Plus className="h-4 w-4" aria-hidden="true" />
          )}
          {editing ? 'Save changes' : 'Add it'}
        </button>

        <button type="button" className="btn-secondary" onClick={onClose}>
          Cancel
        </button>

        {/*
          Only offered for an element that never carried anything. The API
          refuses one with children, tickets or a site attached and says to
          decommission it instead, which keeps the history those tickets point at.
        */}
        {editing && (
          <div className="ml-auto">
            {confirmDelete ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Sure?</span>
                <button
                  type="button"
                  className="btn-secondary text-danger-600"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate()}
                >
                  {remove.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                  Delete it
                </button>
                <button type="button" className="btn-secondary" onClick={() => setConfirmDelete(false)}>
                  No
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="btn-secondary text-danger-600"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
